import CalendarConfig from '../../../../consts/CalendarConfig';
import { applicationViewLabel } from './applicationFigures';

/**
 * Marcações do mapa de aplicação: criação, rótulos, totais e preparo para a
 * figura.
 *
 * Marcação gravada: { view, kind ('point' | 'stroke'), x, y (frações do
 * quadro, 0 a 1), x2, y2 (fim do traço, null no ponto), productId (id do
 * produto da clínica ou null), product, unit, color (copiados do produto na
 * hora), quantity (número), note }. O número é a posição na lista.
 */

export const MARK_KINDS = Object.freeze({ POINT: 'point', STROKE: 'stroke' });

const KIND_LABELS = Object.freeze({ point: 'Ponto', stroke: 'Traço' });

const round4 = (value) => Math.round(value * 10000) / 10000;

const clamp01 = (value) => Math.min(1, Math.max(0, value));

export function markKindLabel(kind) {
  return KIND_LABELS[kind] || kind;
}

const baseMark = (view, product, quantity) => ({
  view,
  productId: product.id == null ? null : Number(product.id),
  product: product.name,
  unit: product.unit || '',
  color: String(product.color || '#5A80FB').toUpperCase(),
  quantity: toQuantity(quantity, product.defaultQuantity),
  note: '',
});

export function createPoint(view, point, product, quantity) {
  return {
    ...baseMark(view, product, quantity),
    kind: MARK_KINDS.POINT,
    x: round4(clamp01(point.x)),
    y: round4(clamp01(point.y)),
    x2: null,
    y2: null,
  };
}

export function createStroke(view, start, end, product, quantity) {
  return {
    ...baseMark(view, product, quantity),
    kind: MARK_KINDS.STROKE,
    x: round4(clamp01(start.x)),
    y: round4(clamp01(start.y)),
    x2: round4(clamp01(end.x)),
    y2: round4(clamp01(end.y)),
  };
}

/** Número válido (aceita vírgula), senão o padrão. */
export function toQuantity(value, fallback = 0) {
  if (value === '' || value === null || value === undefined) return Number(fallback) || 0;
  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) / 100 : Number(fallback) || 0;
}

/** "0,5" / "4" / "12,25" */
export function formatQuantity(value) {
  const number = Number(value) || 0;
  return number.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** "4 UI" */
export function formatAmount(quantity, unit) {
  return [formatQuantity(quantity), unit].filter(Boolean).join(' ');
}

/** "Toxina botulínica · 4 UI" */
export function markSummary(mark) {
  return `${mark.product} · ${formatAmount(mark.quantity, mark.unit)}`;
}

/**
 * Soma por produto (nome + unidade, lidos da própria marcação), na ordem em
 * que cada produto apareceu: [{ product, unit, color, total, count }].
 */
export function totalsByProduct(marks) {
  const totals = [];
  const byKey = new Map();
  marks.forEach((mark) => {
    const key = `${mark.product}|${mark.unit}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { product: mark.product, unit: mark.unit, color: mark.color, total: 0, count: 0 };
      byKey.set(key, entry);
      totals.push(entry);
    }
    entry.total = Math.round((entry.total + (Number(mark.quantity) || 0)) * 100) / 100;
    entry.count += 1;
  });
  return totals;
}

export function formatDate(value) {
  return value ? CalendarConfig.GetDDMMAAAA(value) : '';
}

/** Registros (um por atendimento) do mais recente ao mais antigo. */
export function sortByDateDesc(records) {
  return [...records].sort((a, b) => {
    const byDate = String(b.markedAt || '').localeCompare(String(a.markedAt || ''));
    return byDate !== 0 ? byDate : Number(b.id) - Number(a.id);
  });
}

export function findOwnRecord(records, appointmentId) {
  if (appointmentId == null) return null;
  return records.find((record) => String(record.appointmentId) === String(appointmentId)) || null;
}

/**
 * Marcações de uma vista no formato da figura. `prefix` diferencia as chaves
 * de cada origem (atendimento atual, anteriores); `muted` desenha os
 * anteriores esmaecidos, sem rótulo, com a data no título do balão.
 */
export function toFigureMarks(marks, view, { prefix, selectedIndex = null, highlightedIndex = null, muted = false, title = null } = {}) {
  return marks
    .map((mark, index) => ({ mark, index }))
    .filter(({ mark }) => mark.view === view)
    .map(({ mark, index }) => ({
      key: `${prefix}-${index}`,
      kind: mark.kind,
      x: mark.x,
      y: mark.y,
      x2: mark.x2,
      y2: mark.y2,
      color: mark.color,
      number: index + 1,
      label: formatAmount(mark.quantity, mark.unit),
      muted,
      selected: selectedIndex === index,
      highlighted: highlightedIndex === index,
      title: muted ? `${title || 'Atendimento anterior'} · ${mark.product}` : `${index + 1}. ${mark.product}`,
      details: [
        `${formatAmount(mark.quantity, mark.unit)} · ${markKindLabel(mark.kind)}${mark.view !== view ? ` · ${applicationViewLabel(mark.view)}` : ''}`,
        mark.note,
      ].filter(Boolean),
    }));
}
